import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card, Group, Stack, Text, Title, Button, Box, SimpleGrid, Badge, ActionIcon, Divider, Textarea,
  Center,Loader,Flex,NumberInput,rem,Container,
} from "@mantine/core";
import {
  IconTarget, IconGridDots,IconPencil,IconPlus, IconTrash,
  IconDeviceFloppy,IconCalendar,IconUser,IconEye,IconCheck, IconChevronLeft,
} from "@tabler/icons-react";
import CreateMatrixModal from "./CreateMatrixModal";
import ModalDialog from "../../components/ModalDialog";
import { LoadPeis, SavePeiObjectives, DeletePei, UpdatePeiStatus } from "../../actions/peiActions";
import { loadMatrix ,saveMatrix, loadCategories, loadThemes, deleteMatrix } from "../../actions/matrixActions";
import APP_STATES from "../../enums/AppStates";

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

const resequence = (list) => list.map((o, i) => ({ ...o, Number: i + 1 }));

const normalizeObjectives = (raw = []) => {
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
  };

  return [...(raw || [])]
    .sort((a, b) => {
      const na = toNum(a?.numObjetivo);
      const nb = toNum(b?.numObjetivo);
      if (na !== nb) return na - nb;
      return String(a?.Id ?? '').localeCompare(String(b?.Id ?? ''));
    })
    .map((o, idx) => ({
      Id: o?.Id ?? null,
      Number: Number.isFinite(Number(o?.numObjetivo)) ? Number(o.numObjetivo) : (idx + 1),
      Description: o?.descripcion ?? '',
    }));
};

const PlanObjetivoMatriz = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { data: plans, loading } = useSelector((state) => state.pei);
  const { saving: savingObjectives } = useSelector((state) => state.pei.objectives);
  const { data: matrices, loading: loadingMatrices, deletingId: deletingMatrixId } = useSelector((state) => state.matrix);

  const [notFound, setNotFound] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [objectives, setObjectives] = useState([]);

  const [matrixModalOpen, setMatrixModalOpen] = useState(false);
  const [selectedMatrix, setSelectedMatrix] = useState(null);

  const isDraft = plan?.estado.Id.toUpperCase() === APP_STATES.BORRADOR;
  const isActive = plan?.estado.Id.toUpperCase() === APP_STATES.ACTIVO;
  
  useEffect(() => {
    dispatch(loadCategories(user));
    dispatch(loadThemes(user));
  }, []);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      return;
    }

    if (plans.length > 0) {
      const p = plans.find(pl => (pl.Id || '').toLowerCase() === id.toLowerCase());
      if (p) {
        setPlan(p);
        setObjectives(normalizeObjectives(p.Objetivos));
        dispatch(loadMatrix(p.Id, user));
      } else {
        setNotFound(true);
      }
    } else if (!loading) {
      dispatch(LoadPeis(null, null, user));
    }
  }, [id, plans]);

  //#region (PLAN)
    const openDeleteDialog = () => {
      const isDraft = plan?.estado.Id.toUpperCase() === APP_STATES.BORRADOR;
      if (!isDraft) return;
  
      const data = {
        title: "Confirmar eliminación",
        description: (
          <Text mb="lg">
            ¿Estás seguro que deseas <b>eliminar</b> el plan estratégico {plan.Codigo}? <br /> Esta acción no se puede deshacer.
          </Text>
        ),
        confirmColor: "red",
        onConfirm: () => {handleDeletePlan(plan.Id); setDialogOpen(false);},
      };
  
      setDialogData(data);
      setDialogOpen(true);
    };
    
    const handleDeletePlan = (planId) => {
      dispatch(DeletePei(planId, user)).then(navigate(-1));
    };

    const openActivateDialog = () => {
      const isDraft = plan?.estado.Id.toUpperCase() === APP_STATES.BORRADOR;
      if (!isDraft) return;
  
      const data = {
        title: "Activar Plan Estrategico",
        description: (
          <Text mb="lg">
            Al activar el plan estratégico <b>{plan.Codigo}</b>, 
            el plan activo actual pasará a <b>histórico</b>.
            <br />
            ¿Desea continuar?
          </Text>
        ),
        confirmColor: "blue",
        onConfirm: () => {handleUpdatePlanStatus(plan.Id); setDialogOpen(false);},
      };
  
      setDialogData(data);
      setDialogOpen(true);
    };
    
    const handleUpdatePlanStatus = (planId) => {
      const idStatus = APP_STATES.ACTIVO;
      dispatch(UpdatePeiStatus(planId, idStatus, user));
    };
  //#endregion

  //#region (OBJETIVOS)
  const addObjective = () => {
    setObjectives(prev => resequence([
      ...prev,
      { Id: null, Number: prev.length + 1, Description: '' }
    ]));
  };

  const updateObjective = (index, patch) => {
    setObjectives(prev => {
      const next = prev.map((o, i) => i === index ? { ...o, ...patch } : o);
      return next;
    });
  };

  const updateObjectiveNumber = (index, newNumber) => {
    setObjectives(prev => {
      const max = prev.length;
      const target = Math.min(Math.max(1, toInt(newNumber) || 1), max) - 1;

      const arr = [...prev];
      const [item] = arr.splice(index, 1);
      arr.splice(target, 0, item);

      return resequence(arr);
    });
  };

  const removeObjective = (index) => {
    setObjectives(prev => resequence(prev.filter((_, i) => i !== index)));
  };

  const handleSaveObjectives = () => {
    if (!id) return;

    const payload = objectives.map((o) => ({
      Id: o.Id ?? undefined,
      numObjetivo: o.Number,
      descripcion: o.Description,
    }));

    dispatch(SavePeiObjectives(id, payload, user));
  }

  //#endregion

  //#region (MATRICES)
  const handleCreateMatrix = async (payload) => {
    if(payload){
      const r = await dispatch(saveMatrix(plan.Id,payload, user));
      if(r){
        setMatrixModalOpen(false)
      } 
    }
  };

  const openDeleteMatrixDialog = (matrix) => {
    const isDraft = matrix?.estado.Id.toUpperCase() === APP_STATES.BORRADOR;
    if (!isDraft) return;

    const data = {
      title: "Confirmar eliminación",
      description: `¿Estás seguro que deseas eliminar la matriz ${matrix.nombre} - version: ${matrix.version}? Esta acción no se puede deshacer.`,
      confirmColor: "red",
      onConfirm: () => {handleDeleteMatrix(matrix.Id); setDialogOpen(false);},
    };

    setDialogData(data);
    setDialogOpen(true);
  };
  
  const handleDeleteMatrix = (id) => {
    dispatch(deleteMatrix(id, user)).then(dispatch(loadMatrix(plan.Id, user)));
  };

  const handleEditMatrix = (matrix) => {
    if(!matrix) return;

    setSelectedMatrix(matrix)
    setMatrixModalOpen(true);
  }
//#endregion


  if (!plan && !notFound) {
    return (
      <Center>
        <Loader/>
      </Center>
    );
  }

  if (notFound) {
    return (
      <Box className="container" p="xl">
        <Card p="xl" withBorder radius="xl">
          <Title order={3} mb="sm" style={{ fontWeight: 500 }}>
            Plan no encontrado
          </Title>
          <Text c="dimmed" mb="md">
            No pudimos encontrar un plan estrategico: <b>{id}</b>.
          </Text>
          <Button onClick={() => navigate(-1)}>
            Volver
          </Button>
        </Card>
      </Box>
    );
  }
  
  return (
    <div className="min-h-screen">
        <Container size="80%" py="xl">
          <Box py="lg" my={'sm'} radius="lg" shadow='xs'>
            <Flex>
              <Box flex={1}>
                <Group>
                  <ActionIcon size={'lg'} variant="transparent" color="#00000" onClick={() => navigate(-1)} title="volver">
                    <IconChevronLeft style={{ width: '70%', height: '70%' }} />
                  </ActionIcon>
                  <Box>
                    <Text size="xl">Trabajando sobre el plan <Text fw={700} span>{plan.Codigo}</Text></Text>
                    <Group gap={'xs'}>
                      <Text size="md">Estado actual: </Text>
                      <Badge radius="xl" color={isActive ? "#e72166" : isDraft ? "gray" : 'blue'}>
                        {plan.estado.Codigo}
                      </Badge>
                    </Group>
                  </Box>
                </Group>
              </Box>

              {isDraft && (
                <Card p="md" radius="lg" withBorder>
                  <Group gap={'sm'}>
                    <Button
                      size="sm"
                      variant="subtle"
                      leftSection={<IconCheck size={18}/>}
                      onClick={openActivateDialog}
                    >
                      Activar plan
                    </Button>
                    <Button
                      size="sm"
                      variant="subtle"
                      color="red"
                      leftSection={<IconTrash size={18}/>}
                      onClick={openDeleteDialog}
                    >
                      Eliminar plan
                    </Button>
                  </Group>
                </Card>
              )}
            </Flex>
          </Box>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Card p="lg" radius="lg" shadow='xs'>
              <Group gap="sm" justify="space-between">
                <Group gap="sm">
                  <IconTarget size={18} />
                  <Text size="xl" fw={600}>Objetivos</Text>
                </Group>

                {isDraft && (
                  <Button
                    size="sm"
                    color="#e72166"
                    leftSection={<IconDeviceFloppy size={14} />}
                    onClick={handleSaveObjectives}
                    loading={savingObjectives}
                  >
                    Guardar
                  </Button>
                )}
              </Group>
              <Divider my={'md'}/>
              <Stack gap="sm">
                {objectives.length === 0 ? (
                  <Text ta={'center'} c={"dimmed"} py="lg">
                    No hay objetivos para mostrar
                  </Text>
                ) : objectives.map((obj, index) => (
                  <Card withBorder radius="md" p="md" key={index}>
                    {!isDraft ? (
                      <Flex
                        gap="sm"
                        justify="flex-start"
                        align="flex-start"
                        direction="row"
                        wrap="nowrap"
                      >
                        <Text fw={600} size={rem(25)} c={'#e72166'}>{obj.Number}.</Text>
                        <Text size="md" fw={500}>{obj.Description}</Text>
                      </Flex>
                    ) : (
                      <Group justify="space-between" wrap="nowrap" gap="md">
                        <Flex 
                          gap="xs"
                          justify="flex-start"
                          align="flex-start"
                          direction="row"
                          wrap="wrap"
                          w={'100%'}
                        >
                          <NumberInput
                            placeholder="Numero"
                            flex={1}
                            allowNegative={false}
                            allowDecimal={false}
                            value={obj.Number}
                            onChange={(val) => updateObjectiveNumber(index, val)}
                          />
                          <Textarea 
                            placeholder="Ingrese una descripción para el objetivo"
                            flex={4}
                            autosize 
                            minRows={1}
                            value={obj.Description}
                            onChange={(e) => updateObjective(index, { Description: e.currentTarget.value })}
                          />
                        </Flex>
                        <ActionIcon variant="subtle" color="red.6" title="Eliminar" onClick={(e) => removeObjective(index)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    )}
                  </Card>
                ))}
                {isDraft && (
                  <Button 
                    fullWidth
                    leftSection={<IconPlus size={14} />}
                    radius={'md'}
                    variant="outline"
                    onClick={() => addObjective()}
                    disabled={savingObjectives}
                  >
                    Nuevo Objetivo
                  </Button>
                )}
              </Stack>
            </Card>

            {/* Matrices */}
            <Card p="lg" radius="lg" shadow='xs'>
              <Group justify="space-between" align="center">
                <Group gap="sm" align="center">
                  <IconGridDots size={18} />
                  <Text fz={20} fw={600}>Matrices de Evaluación</Text>
                </Group>

                <Button
                  size="sm"
                  color="#e72166"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setMatrixModalOpen(true)}
                >
                  Nueva Matriz
                </Button>
              </Group>

              <Divider my={'md'}/>

              <Stack gap="sm">
                { loadingMatrices ? (
                  <Text ta={'center'} c={"dimmed"}>
                    Cargando matrices ...
                  </Text>
                ) : matrices.length === 0 ? (
                  <Text ta={'center'} c={"dimmed"}>
                    No hay matrices para mostrar
                  </Text>
                ) : matrices.map((m, index) => {
                  const isMtrActive = m?.estado.Id.toUpperCase() === APP_STATES.ACTIVO;
                  const isMtrDraft = m?.estado.Id.toUpperCase() === APP_STATES.BORRADOR;

                  const isMtrDeleting = m.Id === deletingMatrixId

                  return (
                    <Card key={index} radius="md" p="sm" withBorder>
                      <Flex
                        gap="md"
                        justify="flex-start"
                        align="center"
                        direction="row"
                        wrap="nowrap"
                      >
                        <Stack gap={'md'} flex={1}>
                          <Group justify="space-between" align="flex-start" wrap="nowrap">
                            <Box>
                              <Text size="lg" fw={600} >{`< V.${m.version} >`} {m.nombre}</Text>
                              <Text size="md"> {m.descripcion}</Text>
                            </Box>
                            <Group gap="xs" wrap="nowrap">
                              <ActionIcon color="gray.9" variant="subtle" title="Ver matriz" disabled={isMtrDeleting}>
                                <IconEye style={{ width: '70%', height: '70%' }} />
                              </ActionIcon>
                              {isMtrDraft && (
                                <>
                                <ActionIcon 
                                  variant="subtle" 
                                  title="Editar"
                                  disabled={isMtrDeleting}
                                  onClick={() => handleEditMatrix(m)}
                                >
                                  <IconPencil style={{ width: '70%', height: '70%' }} />
                                </ActionIcon>
                                <ActionIcon 
                                  color="red" 
                                  variant="subtle" 
                                  title="Eliminar" 
                                  onClick={() => openDeleteMatrixDialog(m)}
                                  loading={isMtrDeleting}
                                >
                                  <IconTrash style={{ width: '70%', height: '70%' }} />
                                </ActionIcon>
                                </>
                              )}
                            </Group>
                          </Group>
                          
                          <Group gap={'md'}>
                            <Box>
                              <Text size="xs" c="dimmed">Estado</Text>
                              <Badge size="sm" color={isMtrActive ? "#e72166" : isMtrDraft ? "gray" : 'blue'}>{m?.estado.Codigo}</Badge>
                            </Box>
                            <Box>
                              <Text size="xs" c="dimmed">Usuario actualizacion</Text>
                              <Center inline>
                                <IconUser size={14}/>
                                <Text size="sm" ml={5}>{m?.vc_luuser ?? '[No especificado]'}</Text>
                              </Center>
                            </Box>
                            <Box>
                              <Text size="xs" c="dimmed">Fecha actualizacion</Text>
                              <Center inline>
                                <IconCalendar size={14}/>
                                <Text size="sm" ml={5}>{m?.dt_lu}</Text>
                              </Center>
                            </Box>
                          </Group>
                        </Stack>
                      </Flex>
                    </Card>
                  )
                })}
              </Stack>
            </Card>
          </SimpleGrid>
        </Container>

        {/* Modal Crear Matriz */}
        <CreateMatrixModal
          opened={matrixModalOpen}
          onSave={handleCreateMatrix}
          onClose={() => setMatrixModalOpen(false)}
          objectives={objectives}
          matrix={selectedMatrix}
        />

        <ModalDialog
          opened={dialogOpen}
          onClose = {() => setDialogOpen(false)}
          onConfirm = {() => dialogData?.onConfirm && dialogData.onConfirm()}
          title = {dialogData?.title}
          description = {dialogData?.description}
          confirmColor = {dialogData?.confirmColor}
        />
    </div>
  )
}

export default PlanObjetivoMatriz;
